import {
  LAYOUT_TYPES,
  LEFT_SIDEBAR_SIZE_TYPES,
} from "../Components/constants/layout";

/** Studio shell default — paridade Super Admin (ADJ-F2-131). */
export const TENANT_LAYOUT_TYPE = LAYOUT_TYPES.VERTICAL;

/** Sidebar expandida: ícone + rótulo i18n (`data-sidebar-size="lg"`). */
export const TENANT_SIDEBAR_SIZE = LEFT_SIDEBAR_SIZE_TYPES.DEFAULT;

/** Abaixo disso: overlay hamburger (`vertical-sidebar-enable`); acima: sidebar fixa com rótulos. */
export const VERTICAL_SIDEBAR_MOBILE_MAX_PX = 767;

/**
 * Viewport → `data-sidebar-size` no layout vertical (polish pós-ADJ-F2-131).
 * Desktop **e tablet** (≥768): mantém o tamanho preferido (default `lg` = ícone+nome).
 * Mobile (≤767): `lg` no drawer overlay — hamburger já existente.
 * **Proibido** forçar `sm` no tablet: voltava ao anti-padrão “só ícone” do Two Column.
 */
export function resolveVerticalSidebarSizeForViewport(
  viewportWidthPx: number,
  preferredSize: string = TENANT_SIDEBAR_SIZE
): string {
  if (viewportWidthPx <= VERTICAL_SIDEBAR_MOBILE_MAX_PX) {
    return TENANT_SIDEBAR_SIZE;
  }
  return preferredSize || TENANT_SIDEBAR_SIZE;
}

/**
 * Chaves que podem ter guardado Two Column em browsers legados / experimentos.
 * Limpeza one-shot — layout Adatis é Redux-only; não reintroduzir persistência sem decisão PO.
 */
export const LEGACY_TWO_COLUMN_LAYOUT_STORAGE_KEYS = [
  "adatis.layoutType",
  "adatis.layout",
  "data-layout",
] as const;

const TWO_COLUMN = LAYOUT_TYPES.TWOCOLUMN;

function storageLooksLikeTwoColumn(raw: string | null): boolean {
  if (!raw) {
    return false;
  }
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === TWO_COLUMN || trimmed === `"${TWO_COLUMN}"`) {
    return true;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed === "string") {
      return parsed.toLowerCase() === TWO_COLUMN;
    }
    if (parsed && typeof parsed === "object" && "layoutType" in parsed) {
      return String((parsed as { layoutType: unknown }).layoutType).toLowerCase() === TWO_COLUMN;
    }
  } catch {
    // plain string already checked
  }
  return false;
}

/**
 * Migra preferência legado Two Column → vertical + lg (once).
 * Também limpa `twocolumn-panel` / `data-layout=twocolumn` no DOM se presentes no first paint.
 */
export function migrateLegacyTwoColumnLayout(): {
  layoutType: typeof TENANT_LAYOUT_TYPE;
  leftsidbarSizeType: typeof TENANT_SIDEBAR_SIZE;
  clearedLegacy: boolean;
} {
  let clearedLegacy = false;

  if (typeof window !== "undefined") {
    for (const key of LEGACY_TWO_COLUMN_LAYOUT_STORAGE_KEYS) {
      try {
        if (storageLooksLikeTwoColumn(window.localStorage.getItem(key))) {
          window.localStorage.removeItem(key);
          clearedLegacy = true;
        }
        if (storageLooksLikeTwoColumn(window.sessionStorage.getItem(key))) {
          window.sessionStorage.removeItem(key);
          clearedLegacy = true;
        }
      } catch {
        // storage unavailable
      }
    }

    try {
      if (document.documentElement.getAttribute("data-layout") === TWO_COLUMN) {
        document.documentElement.setAttribute("data-layout", TENANT_LAYOUT_TYPE);
        document.documentElement.setAttribute("data-sidebar-size", TENANT_SIDEBAR_SIZE);
        clearedLegacy = true;
      }
      if (document.body.classList.contains("twocolumn-panel")) {
        document.body.classList.remove("twocolumn-panel");
        clearedLegacy = true;
      }
    } catch {
      // SSR / no document
    }
  }

  return {
    layoutType: TENANT_LAYOUT_TYPE,
    leftsidbarSizeType: TENANT_SIDEBAR_SIZE,
    clearedLegacy,
  };
}
